import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_WINGS, SOCIETY_PORTAL_FLAT_TRANSFER_WIZARD, SOCIETY_PORTAL_FLAT_TRANSFER_HISTORY } from '../../../../Routing/routes';
import { getFlatsList, createFlat, editFlat, deleteFlat, bulkUploadFlats, clearFlatsBulkUploadState } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import BulkUploadModal from './BulkUploadModal';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import Pagination from '../../../../components/common/Pagination';
import { PROPERTY_TYPE_OPTIONS } from './flatFormOptions';
import { canAny } from '../../../../utils/helpers/can';
import { PERMISSIONS } from '../../../../consts/permissions';
import { selectedSocietyStorer } from '../../../../utils/webStorer';

const FLAT_TYPES = [
    { label: '1 BHK', value: '1BHK' },
    { label: '2 BHK', value: '2BHK' },
    { label: '3 BHK', value: '3BHK' },
    { label: '4 BHK', value: '4BHK' },
    { label: '5 BHK', value: '5BHK' },
    { label: 'Studio', value: 'STUDIO' },
    { label: 'Penthouse', value: 'PENTHOUSE' },
    { label: 'Duplex', value: 'DUPLEX' },
    { label: 'Shop', value: 'SHOP' },
    { label: 'Office', value: 'OFFICE' },
    { label: 'Other', value: 'OTHER' }
];

const OWNERSHIP_TYPES = [
    { label: 'Owned', value: 'OWNED' },
    { label: 'Society Owned', value: 'SOCIETY_OWNED' },
    { label: 'Builder Owned', value: 'BUILDER_OWNED' }
];

// Retired values. Flats saved before Builder Owned existed still carry these,
// so lists and the view drawer need labels for them even though the form no
// longer offers them as choices.
const LEGACY_OWNERSHIP_TYPES = [
    { label: 'Rented', value: 'RENTED' },
    { label: 'Vacant', value: 'VACANT' }
];
const ALL_OWNERSHIP_TYPES = [...OWNERSHIP_TYPES, ...LEGACY_OWNERSHIP_TYPES];

const DEFAULT_OWNERSHIP_TYPE = 'BUILDER_OWNED';

const getPropertyTypeLabel = (value) =>
    PROPERTY_TYPE_OPTIONS.find(o => o.value === value)?.label || value;

const FLAT_STATUS = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' },
    { label: 'Under Renovation', value: 'UNDER_RENOVATION' },
    { label: 'Locked', value: 'LOCKED' }
];

const Flats = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [urlSearchParams] = useSearchParams();
    const wingId = urlSearchParams.get('wingId');
    const wingName = urlSearchParams.get('wingName') || 'All Wings';

    const { flatsList, flatsPagination, flatsStats, flatsLoading, flatsCreateLoading, flatsEditLoading, flatsDeleteLoading, flatsBulkUploadLoading, flatsBulkUploadResult } = useSelector(state => state.WingsAndFlatsReducers);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const userPermissions = useSelector(state => state.UserSocietyReducer?.permissions || []);
    const canTransfer = canAny(userPermissions, [PERMISSIONS.FLAT_TRANSFER_CREATE]);
    const canViewTransferHistory = canAny(userPermissions, [PERMISSIONS.FLAT_TRANSFER_READ]);

    const goToTransferWizard = (flatId) => {
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_FLAT_TRANSFER_WIZARD.replace(':flatId', flatId)}`);
    };
    const goToTransferHistory = (flatId) => {
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_FLAT_TRANSFER_HISTORY.replace(':flatId', flatId)}`);
    };

    const [viewMode, setViewMode] = useState('grid');
    const [showModal, setShowModal] = useState(false);
    const [viewFlat, setViewFlat] = useState(null);

    const handleView = (flat) => setViewFlat(flat);
    const closeViewModal = () => setViewFlat(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterOwnership, setFilterOwnership] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchParams, setSearchParams] = useState({ page: 1, limit: 10, wing_id: wingId });

    const [formData, setFormData] = useState({
        flat_number: '',
        floor_number: '',
        flat_type: '2BHK',
        carpet_area: '',
        built_up_area: '',
        super_built_up_area: '',
        facing: '',
        ownership_type: DEFAULT_OWNERSHIP_TYPE,
        status: 'ACTIVE',
        is_parking_allotted: false,
        parking_slot_count: 0,
        property_type: '',
        date_of_admission: '',
        share_certificate_count: '',
        share_certificate_number: '',
        sale_deed_number: ''
    });
    const [editId, setEditId] = useState(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const flats = flatsList || [];

    // Fetch flats on mount and when search params change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (wingId) {
            dispatch(getFlatsList(searchParams));
        }
    }, [dispatch, searchParams, wingId, refreshKey]);

    // Update search params when filters change
    useEffect(() => {
        const newParams = {
            page: 1,
            limit: searchParams.limit,
            wing_id: wingId
        };
        if (searchQuery) newParams.search = searchQuery;
        if (filterType !== 'all') newParams.flat_type = filterType;
        if (filterOwnership !== 'all') newParams.ownership_type = filterOwnership;
        if (filterStatus !== 'all') newParams.status = filterStatus;

        setSearchParams(newParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, filterType, filterOwnership, filterStatus, wingId]);

    // Counted by the server across every flat the filters match, not just the
    // page on screen. Occupied = has an active owner or a current tenant.
    const stats = useMemo(() => ({
        totalFlats: flatsStats?.total ?? flatsPagination?.total ?? flats.length,
        occupied: flatsStats?.occupied ?? 0,
        vacant: flatsStats?.vacant ?? 0,
        underRenovation: flatsStats?.under_renovation ?? 0,
        withParking: flatsStats?.with_parking ?? 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [flats, flatsPagination, flatsStats]);

    const vacancyRate = stats.totalFlats > 0 ? Math.round((stats.vacant / stats.totalFlats) * 100) : 0;
    const occupancyRate = flatsStats?.occupancy_rate ?? (stats.totalFlats > 0 ? Math.round((stats.occupied / stats.totalFlats) * 100) : 0);

    const getOwnershipColor = (type) => {
        switch (type) {
            case 'OWNED': return 'tw-bg-emerald-500 tw-text-white';
            case 'BUILDER_OWNED': return 'tw-bg-blue-500 tw-text-white';
            // Retired values, still worn by rows saved before Builder Owned.
            case 'RENTED': return 'tw-bg-sky-500 tw-text-white';
            case 'VACANT': return 'tw-bg-gray-400 tw-text-white';
            case 'SOCIETY_OWNED': return 'tw-bg-purple-500 tw-text-white';
            default: return 'tw-bg-gray-400 tw-text-white';
        }
    };

    const getOwnershipLabel = (type) => {
        const ownershipType = ALL_OWNERSHIP_TYPES.find(t => t.value === type);
        return ownershipType?.label || type;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'tw-bg-green-100 tw-text-green-700';
            case 'INACTIVE': return 'tw-bg-gray-100 tw-text-gray-700';
            case 'UNDER_RENOVATION': return 'tw-bg-orange-100 tw-text-orange-700';
            case 'LOCKED': return 'tw-bg-red-100 tw-text-red-700';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    const getStatusLabel = (status) => {
        const flatStatus = FLAT_STATUS.find(s => s.value === status);
        return flatStatus?.label || status;
    };

    const getFlatTypeLabel = (type) => {
        const flatType = FLAT_TYPES.find(t => t.value === type);
        return flatType?.label || type;
    };

    const today = localDateString();

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.date_of_admission && formData.date_of_admission > today) {
            window.alert('Date of admission cannot be in the future');
            return;
        }
        // Billing filters tariff heads on this, so a blank value silently skips type-specific charges
        if (!formData.property_type) {
            window.alert('Property type is required');
            return;
        }
        const payload = {
            ...formData,
            wing_id: parseInt(wingId),
            floor_number: parseInt(formData.floor_number),
            carpet_area: formData.carpet_area ? parseFloat(formData.carpet_area) : null,
            built_up_area: formData.built_up_area ? parseFloat(formData.built_up_area) : null,
            super_built_up_area: formData.super_built_up_area ? parseFloat(formData.super_built_up_area) : null,
            parking_slot_count: parseInt(formData.parking_slot_count) || 0,
            // An untouched Facing dropdown holds '', which the API rejects as an
            // invalid enum value — send null for "not specified", the same as
            // every other optional field here.
            facing: formData.facing || null,
            property_type: formData.property_type || null,
            date_of_admission: formData.date_of_admission || null,
            share_certificate_count: formData.share_certificate_count === '' ? null : parseInt(formData.share_certificate_count),
            share_certificate_number: formData.share_certificate_number || null,
            sale_deed_number: formData.sale_deed_number || null,
            onSuccess: () => {
                handleCloseModal();
                dispatch(getFlatsList(searchParams));
            }
        };

        if (editId) {
            dispatch(editFlat({ id: editId, ...payload }));
        } else {
            dispatch(createFlat(payload));
        }
    };

    const handleEdit = (flat) => {
        setFormData({
            flat_number: flat.flat_number || '',
            floor_number: flat.floor_number || '',
            flat_type: flat.flat_type || '2BHK',
            carpet_area: flat.carpet_area || '',
            built_up_area: flat.built_up_area || '',
            super_built_up_area: flat.super_built_up_area || '',
            facing: flat.facing || '',
            ownership_type: flat.ownership_type || DEFAULT_OWNERSHIP_TYPE,
            status: flat.status || 'ACTIVE',
            is_parking_allotted: flat.is_parking_allotted || false,
            parking_slot_count: flat.parking_slot_count || 0,
            property_type: flat.property_type || '',
            date_of_admission: flat.date_of_admission ? flat.date_of_admission.split('T')[0] : '',
            share_certificate_count: flat.share_certificate_count ?? '',
            share_certificate_number: flat.share_certificate_number || '',
            sale_deed_number: flat.sale_deed_number || ''
        });
        setEditId(flat.flat_id);
        setShowModal(true);
    };

    const handleDelete = (flatId) => {
        if (window.confirm('Are you sure you want to delete this flat?')) {
            dispatch(deleteFlat({
                id: flatId,
                onSuccess: () => {
                    dispatch(getFlatsList(searchParams));
                }
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            flat_number: '',
            floor_number: '',
            flat_type: '2BHK',
            carpet_area: '',
            built_up_area: '',
            super_built_up_area: '',
            facing: '',
            ownership_type: DEFAULT_OWNERSHIP_TYPE,
            status: 'ACTIVE',
            is_parking_allotted: false,
            parking_slot_count: 0,
            property_type: '',
            date_of_admission: '',
            share_certificate_count: '',
            share_certificate_number: '',
            sale_deed_number: ''
        });
        setEditId(null);
    };

    const handleBack = () => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_WINGS}`);

    const clearFilters = () => {
        setSearchQuery('');
        setFilterType('all');
        setFilterOwnership('all');
        setFilterStatus('all');
    };

    const hasActiveFilters = searchQuery || filterType !== 'all' || filterOwnership !== 'all' || filterStatus !== 'all';

    const handlePageChange = (newPage) => {
        setSearchParams({ ...searchParams, page: newPage });
    };
    // Changing page size always jumps back to page 1 — otherwise the current
    // page number might exceed the new totalPages and the list appears empty.
    const handleItemsPerPageChange = (newLimit) => {
        setSearchParams({ ...searchParams, page: 1, limit: newLimit });
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button onClick={handleBack} className="tw-p-1.5 tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                        <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Flats - {decodeURIComponent(wingName)}</h1>
                        <p className="tw-text-xs tw-text-gray-500">Manage flats and units</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={() => { dispatch(clearFlatsBulkUploadState()); setShowBulkUpload(true); }}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-emerald-700 tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-emerald-100"
                    >
                        <Icon icon="mdi:file-excel-outline" />
                        Upload Excel
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" />
                        Add Flat
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-3 tw-mb-4">
                {[
                    { icon: 'mdi:door', color: 'tw-bg-blue-500', value: stats.totalFlats, label: 'Total Flats' },
                    { icon: 'mdi:check-circle', color: 'tw-bg-emerald-500', value: stats.occupied, label: 'Occupied', sub: `${occupancyRate}% occupancy` },
                    { icon: 'mdi:home-outline', color: 'tw-bg-amber-500', value: stats.vacant, label: 'Vacant', sub: `${vacancyRate}% vacancy` },
                    { icon: 'mdi:tools', color: 'tw-bg-pink-500', value: stats.underRenovation, label: 'Under Renovation' },
                    { icon: 'mdi:car', color: 'tw-bg-purple-500', value: stats.withParking, label: 'With Parking' },
                ].map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-px-3 tw-py-2.5 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <div className={`tw-w-9 tw-h-9 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                <Icon icon={stat.icon} className="tw-text-white tw-text-base" />
                            </div>
                            <div className="tw-min-w-0">
                                <div className="tw-flex tw-items-baseline tw-gap-1">
                                    <span className="tw-text-base tw-font-bold tw-text-gray-800">{stat.value}</span>
                                    <span className="tw-text-[11px] tw-text-gray-500 tw-truncate">{stat.label}</span>
                                </div>
                                {stat.sub && <p className="tw-text-[10px] tw-text-emerald-600 tw-leading-tight">{stat.sub}</p>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search by flat number..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                >
                    <option value="all">All Types</option>
                    {FLAT_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
                <select
                    value={filterOwnership}
                    onChange={(e) => setFilterOwnership(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                >
                    <option value="all">All Ownership</option>
                    {OWNERSHIP_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                >
                    <option value="all">All Status</option>
                    {FLAT_STATUS.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
                <div className="tw-flex tw-bg-gray-100 tw-rounded-lg tw-p-1">
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all ${viewMode === 'grid' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                    >
                        <Icon icon="mdi:view-grid" className="tw-text-base" />
                        <span className="tw-hidden sm:tw-inline">Grid</span>
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all ${viewMode === 'list' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                    >
                        <Icon icon="mdi:view-list" className="tw-text-base" />
                        <span className="tw-hidden sm:tw-inline">List</span>
                    </button>
                </div>
                <span className="tw-text-[11px] tw-text-gray-400 tw-ml-auto">
                    Showing {flats.length} of {flatsPagination?.totalItems || flats.length}
                </span>
            </div>

            {/* Loading State */}
            {flatsLoading && (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-10">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                </div>
            )}

            {/* Grid View */}
            {!flatsLoading && viewMode === 'grid' && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    {flats.map((flat) => (
                        <div key={flat.flat_id} className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow tw-flex tw-flex-col">
                            {/* Card Header */}
                            <div className="tw-p-4 tw-pb-3">
                                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                    <div className="tw-flex tw-items-center tw-gap-3">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon="mdi:door" className="tw-text-white tw-text-lg" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm">{flat.flat_number}</h4>
                                            <p className="tw-text-xs tw-text-gray-500">{getFlatTypeLabel(flat.flat_type)} • Floor {flat.floor_number}</p>
                                        </div>
                                    </div>
                                    <span className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-semibold tw-whitespace-nowrap ${getOwnershipColor(flat.ownership_type)}`}>
                                        {getOwnershipLabel(flat.ownership_type)}
                                    </span>
                                </div>
                            </div>

                            {/* Card Body */}
                            <div className="tw-px-4 tw-pb-3 tw-flex-1">
                                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                    <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusColor(flat.status)}`}>
                                        {getStatusLabel(flat.status)}
                                    </span>
                                    {flat.property_type ? (
                                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
                                            <Icon icon="mdi:office-building-outline" className="tw-text-gray-400 tw-text-sm" /> {getPropertyTypeLabel(flat.property_type)}
                                        </span>
                                    ) : (
                                        <span
                                            className="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-amber-200"
                                            title="No property type set - this flat is only charged heads that apply to all flats"
                                        >
                                            <Icon icon="mdi:alert-outline" className="tw-text-xs" />
                                            No property type
                                        </span>
                                    )}
                                    {flat.facing && (
                                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
                                            <Icon icon="mdi:compass" className="tw-text-gray-400 tw-text-sm" /> {flat.facing}
                                        </span>
                                    )}
                                </div>

                                {/* Area Info */}
                                {(flat.carpet_area || flat.built_up_area || flat.super_built_up_area) && (
                                    <div className="tw-mt-2 tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:ruler-square" className="tw-text-gray-400 tw-text-sm" />
                                        {flat.super_built_up_area ? `${flat.super_built_up_area} sqft` : flat.built_up_area ? `${flat.built_up_area} sqft` : `${flat.carpet_area} sqft`}
                                    </div>
                                )}

                                {/* Parking Info */}
                                {flat.is_parking_allotted && (
                                    <div className="tw-mt-2 tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:car" className="tw-text-gray-400 tw-text-sm" />
                                        {flat.parking_slot_count} Parking Slot{flat.parking_slot_count > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>

                            {/* Card Footer */}
                            <div className="tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                                <div className="tw-flex tw-items-center tw-gap-1">
                                    <button onClick={() => handleView(flat)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View">
                                        <Icon icon="mdi:eye-outline" className="tw-text-base" />
                                    </button>
                                    <button onClick={() => handleEdit(flat)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="Edit">
                                        <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                    </button>
                                    {canTransfer && (
                                        <button onClick={() => goToTransferWizard(flat.flat_id)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-emerald-600 tw-transition-colors" title="Transfer Ownership">
                                            <Icon icon="mdi:swap-horizontal" className="tw-text-base" />
                                        </button>
                                    )}
                                    {canViewTransferHistory && (
                                        <button onClick={() => goToTransferHistory(flat.flat_id)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Transfer History">
                                            <Icon icon="mdi:history" className="tw-text-base" />
                                        </button>
                                    )}
                                    <button onClick={() => handleDelete(flat.flat_id)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors" title="Delete" disabled={flatsDeleteLoading}>
                                        <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* List View */}
            {!flatsLoading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Flat</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Type</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Property Type</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Ownership</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Floor</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Area (sqft)</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Parking</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {flats.map((flat) => (
                                <tr key={flat.flat_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-1.5">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Icon icon="mdi:door" className="tw-text-white tw-text-sm" />
                                            </div>
                                            <div className="tw-min-w-0">
                                                <div className="tw-flex tw-items-center tw-gap-1.5">
                                                    <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-truncate">{flat.flat_number}</h4>
                                                </div>
                                                <p className="tw-text-xs tw-text-gray-500">{getFlatTypeLabel(flat.flat_type)}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-gray-600">{getFlatTypeLabel(flat.flat_type)}</td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        {flat.property_type ? (
                                            <span className="tw-text-gray-600">{getPropertyTypeLabel(flat.property_type)}</span>
                                        ) : (
                                            // Billing filters charge heads on property type - without it this flat misses type-specific heads
                                            <span
                                                className="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-amber-200"
                                                title="No property type set - this flat is only charged heads that apply to all flats"
                                            >
                                                <Icon icon="mdi:alert-outline" className="tw-text-xs" />
                                                Not set
                                            </span>
                                        )}
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getOwnershipColor(flat.ownership_type)}`}>
                                            {getOwnershipLabel(flat.ownership_type)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusColor(flat.status)}`}>
                                            {getStatusLabel(flat.status)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-text-gray-600">{flat.floor_number}</td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-text-gray-600">
                                        {flat.super_built_up_area || flat.built_up_area || flat.carpet_area || '-'}
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5 tw-text-center">
                                        {flat.is_parking_allotted ? (
                                            <span className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-emerald-600">
                                                <Icon icon="mdi:check-circle" className="tw-text-xs" /> {flat.parking_slot_count}
                                            </span>
                                        ) : (
                                            <span className="tw-text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="tw-px-3 tw-py-1.5">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                            <button onClick={() => handleView(flat)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            <button onClick={() => handleEdit(flat)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400" title="Edit">
                                                <Icon icon="mdi:pencil-outline" />
                                            </button>
                                            {canTransfer && (
                                                <button onClick={() => goToTransferWizard(flat.flat_id)} className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600" title="Transfer Ownership">
                                                    <Icon icon="mdi:swap-horizontal" />
                                                </button>
                                            )}
                                            {canViewTransferHistory && (
                                                <button onClick={() => goToTransferHistory(flat.flat_id)} className="tw-p-1.5 tw-rounded hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600" title="Transfer History">
                                                    <Icon icon="mdi:history" />
                                                </button>
                                            )}
                                            <button onClick={() => handleDelete(flat.flat_id)} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500" title="Delete" disabled={flatsDeleteLoading}>
                                                <Icon icon="mdi:trash-can-outline" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {flats.length === 0 && (
                        <div className="tw-text-center tw-py-8">
                            <Icon icon="mdi:door-open" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No flats found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination — shared component so the page-size selector, "N of
                M" info, and jump-to-first/last buttons are consistent with
                the other list pages. Renders even when totalPages == 1 so
                users can still change how many rows they see per page. */}
            {!flatsLoading && flatsPagination && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={flatsPagination.page || 1}
                        totalPages={flatsPagination.totalPages || 1}
                        totalItems={flatsPagination.totalItems || flats.length}
                        itemsPerPage={searchParams.limit}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        itemsPerPageOptions={[10, 20, 50, 100]}
                        itemLabel="flats"
                    />
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-2xl tw-shadow-xl tw-max-h-[90vh] tw-overflow-y-auto">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-sticky tw-top-0 tw-bg-white">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                                    <Icon icon="mdi:door" className="tw-text-white tw-text-sm" />
                                </div>
                                <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">{editId ? 'Edit Flat' : 'Add New Flat'}</h3>
                            </div>
                            <button onClick={handleCloseModal} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors">
                                <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="tw-p-5">
                            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Flat Number <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.flat_number}
                                        onChange={(e) => setFormData({ ...formData, flat_number: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="101"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Floor Number <span className="tw-text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        value={formData.floor_number}
                                        onChange={(e) => setFormData({ ...formData, floor_number: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="1"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Flat Type <span className="tw-text-red-500">*</span></label>
                                    <SearchableDropdown
                                        value={formData.flat_type}
                                        onChange={(val) => setFormData({ ...formData, flat_type: val })}
                                        options={FLAT_TYPES}
                                        placeholder="Select Flat Type"
                                        clearable={false}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Ownership Type</label>
                                    <SearchableDropdown
                                        value={formData.ownership_type}
                                        onChange={(val) => setFormData({ ...formData, ownership_type: val })}
                                        options={OWNERSHIP_TYPES}
                                        placeholder="Select Ownership"
                                        clearable={false}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Status</label>
                                    <SearchableDropdown
                                        value={formData.status}
                                        onChange={(val) => setFormData({ ...formData, status: val })}
                                        options={FLAT_STATUS}
                                        placeholder="Select Status"
                                        clearable={false}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Carpet Area (sqft)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.carpet_area}
                                        onChange={(e) => setFormData({ ...formData, carpet_area: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="850.50"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Built-up Area (sqft)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.built_up_area}
                                        onChange={(e) => setFormData({ ...formData, built_up_area: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="1050.75"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Super Built-up Area (sqft)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.super_built_up_area}
                                        onChange={(e) => setFormData({ ...formData, super_built_up_area: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="1250.00"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Parking Slots</label>
                                    <input
                                        type="number"
                                        value={formData.parking_slot_count}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            parking_slot_count: e.target.value,
                                            is_parking_allotted: parseInt(e.target.value) > 0
                                        })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="0"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                        Property Type <span className="tw-text-red-500">*</span>
                                    </label>
                                    <SearchableDropdown
                                        value={formData.property_type}
                                        onChange={(val) => setFormData({ ...formData, property_type: val })}
                                        options={PROPERTY_TYPE_OPTIONS}
                                        placeholder="Select Property Type"
                                    />
                                    {!formData.property_type && (
                                        <p className="tw-mt-1 tw-text-xs tw-text-amber-600">
                                            Required for billing - a flat without a property type is only charged heads that apply to all flats.
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Date of Admission</label>
                                    <input
                                        type="date"
                                        value={formData.date_of_admission}
                                        onChange={(e) => setFormData({ ...formData, date_of_admission: e.target.value })}
                                        max={today}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Sale Deed Number</label>
                                    <input
                                        type="text"
                                        value={formData.sale_deed_number}
                                        onChange={(e) => setFormData({ ...formData, sale_deed_number: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="SD-2024-001"
                                        maxLength={100}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Number of Share Certificates</label>
                                    <input
                                        type="number"
                                        value={formData.share_certificate_count}
                                        onChange={(e) => setFormData({ ...formData, share_certificate_count: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="0"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Share Certificate Number</label>
                                    <input
                                        type="text"
                                        value={formData.share_certificate_number}
                                        onChange={(e) => setFormData({ ...formData, share_certificate_number: e.target.value })}
                                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                        placeholder="SC-001"
                                        maxLength={50}
                                    />
                                </div>
                            </div>
                            <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100">
                                <button type="button" onClick={handleCloseModal} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-text-gray-800">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="tw-px-4 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2"
                                    disabled={flatsCreateLoading || flatsEditLoading}
                                >
                                    {(flatsCreateLoading || flatsEditLoading) && (
                                        <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-b-2 tw-border-white"></div>
                                    )}
                                    {editId ? 'Update' : 'Add Flat'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Flat Modal — read-only details */}
            {viewFlat && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4" onClick={closeViewModal}>
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                                    <Icon icon="mdi:door" className="tw-text-white tw-text-xl" />
                                </div>
                                <div>
                                    <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">Flat {viewFlat.flat_number}</h3>
                                    <p className="tw-text-[11px] tw-text-gray-500">{getFlatTypeLabel(viewFlat.flat_type)} · Floor {viewFlat.floor_number}</p>
                                </div>
                            </div>
                            <button onClick={closeViewModal} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                                <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="tw-p-5 tw-overflow-y-auto" style={{ maxHeight: 'calc(90vh - 130px)' }}>
                            {/* Status badges */}
                            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getOwnershipColor(viewFlat.ownership_type)}`}>
                                    {getOwnershipLabel(viewFlat.ownership_type)}
                                </span>
                                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusColor(viewFlat.status)}`}>
                                    {getStatusLabel(viewFlat.status)}
                                </span>
                            </div>

                            {/* Details grid */}
                            <div className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-3">
                                {[
                                    ['Flat Number', viewFlat.flat_number],
                                    ['Floor', viewFlat.floor_number],
                                    ['Flat Type', getFlatTypeLabel(viewFlat.flat_type)],
                                    ['Facing', viewFlat.facing || '-'],
                                    ['Carpet Area (sqft)', viewFlat.carpet_area || '-'],
                                    ['Built-up Area (sqft)', viewFlat.built_up_area || '-'],
                                    ['Super Built-up Area (sqft)', viewFlat.super_built_up_area || '-'],
                                    ['Property Type', viewFlat.property_type ? getPropertyTypeLabel(viewFlat.property_type) : '-'],
                                    ['Ownership Type', getOwnershipLabel(viewFlat.ownership_type)],
                                    ['Status', getStatusLabel(viewFlat.status)],
                                    ['Parking Allotted', viewFlat.is_parking_allotted ? `Yes (${viewFlat.parking_slot_count || 0} slot${(viewFlat.parking_slot_count || 0) > 1 ? 's' : ''})` : 'No'],
                                    ['Date of Admission', viewFlat.date_of_admission ? String(viewFlat.date_of_admission).split('T')[0] : '-'],
                                    ['Share Certificate No.', viewFlat.share_certificate_number || '-'],
                                    ['Share Certificate Count', viewFlat.share_certificate_count ?? '-'],
                                    ['Sale Deed No.', viewFlat.sale_deed_number || '-'],
                                ].map(([label, value]) => (
                                    <div key={label}>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-0.5">{label}</p>
                                        <p className="tw-text-xs tw-text-gray-800 tw-font-medium tw-break-words">{value || '-'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="tw-p-3 tw-border-t tw-border-gray-100 tw-flex tw-justify-end tw-gap-2 tw-bg-gray-50/50">
                            <button onClick={closeViewModal} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-white">
                                Close
                            </button>
                            <button
                                onClick={() => { handleEdit(viewFlat); closeViewModal(); }}
                                className="tw-px-4 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-1.5"
                            >
                                <Icon icon="mdi:pencil-outline" /> Edit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Upload Modal */}
            {showBulkUpload && (
                <BulkUploadModal
                    title="Bulk Upload Flats"
                    description="Upload an Excel file to create multiple flats at once"
                    templateName="flats_bulk_upload_sample.xlsx"
                    templateHeaders={['wing_title', 'flat_number', 'floor_number', 'flat_type', 'carpet_area', 'built_up_area', 'super_built_up_area', 'ownership_type', 'status', 'is_parking_allotted', 'parking_slot_count', 'property_type', 'date_of_admission', 'share_certificate_count', 'share_certificate_number', 'sale_deed_number']}
                    templateSample={['Wing A', 'A-101', 1, '2BHK', 650, 750, 900, 'OWNED', 'ACTIVE', 'true', 1, 'RESIDENTIAL', '2024-01-15', 1, 'SC-001', 'SD-001']}
                    templateNotes={['Wing title as it appears in Wing Management (case-insensitive)', '', '', '1BHK / 2BHK / 3BHK / 4BHK / STUDIO / OTHER', '', '', '', 'OWNED / SOCIETY_OWNED / BUILDER_OWNED', 'ACTIVE / INACTIVE / UNDER_RENOVATION / LOCKED', 'true / false', '', '', 'YYYY-MM-DD', '', '', '']}
                    onClose={() => { setShowBulkUpload(false); dispatch(clearFlatsBulkUploadState()); }}
                    onUpload={(file) => dispatch(bulkUploadFlats({
                        society_id: selectedSocietyStorer.getSocietyId(),
                        wing_id: wingId,
                        file,
                        onSuccess: () => dispatch(getFlatsList(searchParams)),
                    }))}
                    uploading={flatsBulkUploadLoading}
                    result={flatsBulkUploadResult}
                />
            )}
        </>
    );
};

export default Flats;
